import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { LoadingComponent } from "@gui/widgets/LoadingComponent";
import { AsyncStatus } from "@gui/widgets/A11y";
import { LoginProps } from "./login.types";
import { useLoginViewModel } from "./login.vm";
import "./login.css";

export function LoginForm(props: LoginProps) {
	const vm = useLoginViewModel(props);

	return (
		<Box
			className="login-form"
			data-testid="login-form"
			maxWidth="md"
			margin="auto"
			component="form"
			onSubmit={vm.handleSubmit}
			noValidate
		>
			<TextField
				variant="outlined"
				margin="normal"
				required
				error={!!vm.emailError}
				disabled={vm.loading}
				fullWidth
				id="email"
				label="Email"
				name="email"
				autoComplete="email"
				autoFocus
				value={vm.username}
				onChange={vm.handleEmailChange}
				data-sentry-component="Email Field"
				// On the *inner* <input>, not TextField's wrapper — a testid on the root would
				// resolve to a div that Playwright cannot fill().
				slotProps={{ htmlInput: { "data-testid": "login-email" } }}
			/>
			<TextField
				variant="outlined"
				margin="normal"
				error={!!vm.passwordError}
				required
				disabled={vm.loading}
				fullWidth
				name="password"
				label="Password"
				type="password"
				id="password"
				autoComplete="current-password"
				value={vm.password}
				onChange={vm.handlePasswordChange}
				data-sentry-component="Password Field"
				slotProps={{ htmlInput: { "data-testid": "login-password" } }}
			/>
			<Button
				className="submit-button"
				type="submit"
				disabled={vm.isSubmitDisabled}
				fullWidth
				variant="contained"
				data-sentry-component="Submit Button"
				data-testid="login-submit"
			>
				Sign In
			</Button>
			{/* Rendered only when populated. Sign-in failures currently surface as toasts (which carry
			    their own role="alert"), so this element is empty in practice — and an always-mounted
			    empty alert is both announced as nothing by some AT and a second, ambiguous match for
			    any `getByRole("alert")`. Kept, with the role, so it is announced rather than silently
			    painted if `setError` is ever given a message. */}
			{vm.error && (
				<Typography className="error-message" variant="body2" color="textSecondary" align="center" role="alert">
					{vm.error}
				</Typography>
			)}
			{vm.loading && (
				<Box display="flex" justifyContent="center" alignItems="center" aria-busy="true">
					<AsyncStatus state="loading" loadingText="Signing in…" />
					<LoadingComponent />
				</Box>
			)}
			<Box className="button-container" display="flex">
				<Button
					disabled={vm.loading}
					onClick={vm.navigateToRegister}
					color="primary"
					variant="contained"
					data-sentry-component="Register Button"
				>
					Register
				</Button>
				<Button
					disabled={vm.loading}
					onClick={vm.navigateToForgotPassword}
					color="primary"
					variant="outlined"
					data-sentry-component="Forgot Password Button"
				>
					Forgot Password
				</Button>
			</Box>
		</Box>
	);
}
